import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Circle, X, Sparkles } from 'lucide-react';
import theme from '../theme';

/**
 * First-time checklist for new users. Lives on the dashboard.
 * Dismissible per user via localStorage key `firstTimeChecklist:<role>:<userId>`.
 *
 * BUG #104 — Creator checklist progress is derived from persisted account
 * data via `GET /api/creator/dashboard/checklist`, so it stays accurate
 * across refresh / logout / login and never asks the creator to redo work.
 * Non-creator roles keep the legacy client-only checklist (unchanged behaviour).
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGACY_ROLE_ITEMS = {
  buyer: [
    { label: 'Browse the marketplace',           to: '/buyer/marketplace' },
    { label: 'View your owned NFTs',             to: '/buyer/dashboard' },
    { label: 'See your transfers',               to: '/buyer/transfers' },
    { label: 'Check buyer payouts',              to: '/buyer/payouts' },
    { label: 'Open buyer settings',              to: '/buyer/settings' },
  ],
  broker: [
    { label: 'Browse open opportunities',        to: '/broker/dashboard' },
    { label: 'See your active portfolios',       to: '/broker/portfolios' },
    { label: 'Visit the broker payouts page',    to: '/broker/payouts' },
    { label: 'Check the broker directory',       to: '/broker/directory' },
    { label: 'Review broker disputes',           to: '/broker/disputes' },
  ],
  admin: [
    { label: 'Review platform stats',            to: '/admin/dashboard?tab=overview' },
    { label: 'Triage open disputes',             to: '/admin/dashboard?tab=disputes' },
    { label: 'Invite a buyer',                   to: '/admin/dashboard?tab=invites' },
    { label: 'Approve creators',                 to: '/admin/dashboard?tab=approvals' },
    { label: 'Manage plans & pricing',           to: '/admin/dashboard?tab=plans' },
  ],
};

export default function FirstTimeChecklist({ role, userId }) {
  const navigate = useNavigate();
  const storageKey = `firstTimeChecklist:${role}:${userId || 'anon'}`;
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(storageKey));
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey + ':checked') || '[]'); }
    catch { return []; }
  });

  // Creator: server-derived items. Non-creator roles: legacy client items.
  const [serverItems, setServerItems] = useState(null);
  const isCreator = role === 'creator' || role === 'buyer';

  useEffect(() => {
    if (!isCreator) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/${role === 'buyer' ? 'buyer' : 'creator'}/dashboard/checklist`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (!cancelled && r?.data?.items) setServerItems(r.data);
    }).catch(() => { /* fall through — hide silently */ });
    return () => { cancelled = true; };
  }, [isCreator]);

  if (dismissed) return null;

  // Determine which items to render.
  let items = [];
  let doneCount = 0;
  let pct = 0;
  if (isCreator) {
    if (!serverItems) return null; // wait for real progress; avoid misleading 0%
    items = serverItems.items || [];
    doneCount = serverItems.done_count ?? 0;
    pct = serverItems.percent ?? 0;
  } else {
    items = LEGACY_ROLE_ITEMS[role] || [];
    if (items.length === 0) return null;
    doneCount = items.filter((it) => checked.includes(it.label)).length;
    pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;
  }

  const toggleLegacy = (label) => {
    const next = checked.includes(label) ? checked.filter(l => l !== label) : [...checked, label];
    setChecked(next);
    localStorage.setItem(storageKey + ':checked', JSON.stringify(next));
  };
  const dismiss = () => { localStorage.setItem(storageKey, '1'); setDismissed(true); };

  return (
    <div data-testid="first-time-checklist"
      style={{
        background: 'linear-gradient(135deg, rgba(0,102,255,0.06), rgba(0,102,255,0.02))',
        border: `1px solid ${theme.colors.primary}33`,
        borderRadius: theme.radius.card,
        padding: '18px 22px',
        marginBottom: '24px',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Sparkles size={18} color={theme.colors.primary} />
          <p data-testid="checklist-progress" style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>
            First-time checklist — {pct}% done ({doneCount} of {isCreator && serverItems ? serverItems.total : items.length}{role === 'buyer' ? ' required steps' : ''})
            {role === 'buyer' && serverItems?.optional_count > 0 && <span style={{ fontWeight: 500, fontSize: 12, color: theme.colors.text.muted }}> · {serverItems.optional_count} optional</span>}
          </p>
        </div>
        <button data-testid="checklist-dismiss" onClick={dismiss}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.text.muted }}>
          <X size={16} />
        </button>
      </div>
      <div style={{ height: '4px', background: 'rgba(0,0,0,0.05)', borderRadius: '2px', overflow: 'hidden', marginBottom: '14px' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: theme.colors.primary, transition: 'width 0.3s' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {items.map((it, idx) => {
          const done = isCreator ? !!it.done : checked.includes(it.label);
          const goTo = it.to;
          const viewTo = it.view_to || it.to;
          const description = it.description || null;
          const prereq = !done && it.prereq_message ? it.prereq_message : null;
          return (
            <div key={it.key || it.label} data-testid={`checklist-item-${idx + 1}`}
              style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                gap: 12, padding: '10px 10px', borderRadius: theme.radius.sm }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <button
                  onClick={() => { if (!isCreator) toggleLegacy(it.label); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'transparent', border: 'none',
                    cursor: isCreator ? 'default' : 'pointer',
                    color: done ? theme.colors.text.muted : theme.colors.text.primary,
                    textDecoration: done ? 'line-through' : 'none', fontSize: '14px',
                    padding: 0, textAlign: 'left', width: '100%',
                  }}>
                  {done
                    ? <CheckCircle2 size={16} color={theme.colors.status.success} />
                    : <Circle size={16} color={theme.colors.text.muted} />}
                  <span style={{ fontWeight: 600 }}>{it.label}</span>
                  {done && (
                    <span data-testid={`checklist-item-${idx + 1}-completed`} style={{
                      marginLeft: 6, fontSize: 11, fontWeight: 700, color: theme.colors.status.success,
                      background: `${theme.colors.status.success}1A`,
                      padding: '2px 8px', borderRadius: 999, letterSpacing: 0.3,
                      textDecoration: 'none',
                    }}>Completed ✓</span>
                  )}
                </button>
                {description && !done && (
                  <p data-testid={`checklist-item-${idx + 1}-desc`} style={{
                    margin: '4px 0 0 26px', fontSize: 12, color: theme.colors.text.muted,
                  }}>{description}</p>
                )}
                {prereq && (
                  <p data-testid={`checklist-item-${idx + 1}-prereq`} style={{
                    margin: '4px 0 0 26px', fontSize: 12, color: theme.colors.status.warning, fontWeight: 600,
                  }}>{prereq}</p>
                )}
              </div>
              {done ? (
                <button data-testid={`checklist-view-${idx + 1}`}
                  onClick={() => navigate(viewTo)}
                  style={{ background: 'transparent', color: theme.colors.primary,
                    border: `1px solid ${theme.colors.primary}55`,
                    borderRadius: theme.radius.sm, padding: '6px 12px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 600, alignSelf: 'flex-start' }}>
                  View →
                </button>
              ) : (
                <button data-testid={`checklist-go-${idx + 1}`}
                  onClick={() => navigate(goTo)}
                  style={{ background: theme.colors.primary, color: '#FFF', border: 'none', borderRadius: theme.radius.sm, padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, alignSelf: 'flex-start' }}>
                  Go →
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
